import request from '@/utils/http'

/**
 * Tab1 题型胶囊 + 更新时间
 * GET /api/postsV2/highFrequencyQuestionList
 * data 结构为 [{ fcode, viewList }]，按科目分组
 */
export const getHTypeApi = () => {
  return request.get<HList[]>('/api/postsV2/highFrequencyQuestionList')
}

export interface HList {
  fcode: string
  viewList: ViewList[]
}

export interface ViewList {
  typeCode: string
  count: number
  hitRate: number
  stabilityRate: number
  highFrequency: number
  proposal: string
  eproposal: string
  /** 周区间文案（如 8.24-8.30），不要当精确更新时间 */
  date: string
  /** forecast 最新 create_time，毫秒。格式化为「更新于 MM-dd HH:mm」 */
  hfUpdateTime?: number
  newQuestionType: number
  thisWeekNewlyIncreased?: number
  dailyForecast?: number
  termId: number
  questionsTypeCodeDesc?: string
  slugDesc?: string
}

/**
 * Tab1 羊驼高频列表
 * POST /api/postsV2/highFrequencyList
 */
export const getQListApi = (payload: Record<string, unknown>) => {
  // 清理空值字段，避免发送无意义的筛选参数
  const cleaned: Record<string, unknown> = {}
  Object.keys(payload).forEach((key) => {
    const val = payload[key]
    if (val === undefined || val === null || val === '') return
    // 考频多选：逗号拼接字符串转数组
    if (key === 'hots' && typeof val === 'string') {
      cleaned[key] = val.split(',').map(Number).filter(Boolean)
      return
    }
    // 收藏色多选：逗号拼接字符串转数组
    if (key === 'colors' && typeof val === 'string') {
      cleaned[key] = val.split(',').map(Number).filter(Boolean)
      return
    }
    // 难度：1易/2中/3难，字符串转数字
    if (key === 'difficulty' && typeof val !== 'object') {
      const n = Number(val)
      if (!Number.isNaN(n)) cleaned[key] = n
      return
    }
    if (key === 'imageType' && typeof val === 'string') {
      cleaned[key] = decodeURI(val)
      return
    }
    cleaned[key] = val
  })
  return request.post<QListData>('/api/postsV2/highFrequencyList', cleaned)
}

/**
 * Tab2/Tab3 七日冲刺预测 / 七日变动汇总
 * POST /api/postsV2/sprintForecastList
 */
export const getSprintForecastApi = (payload: Record<string, unknown>) => {
  const cleaned: Record<string, unknown> = {}
  Object.keys(payload).forEach((key) => {
    const val = payload[key]
    if (val === undefined || val === null || val === '') return
    if (key === 'hots' && typeof val === 'string') {
      cleaned[key] = val.split(',').map(Number).filter(Boolean)
      return
    }
    if (key === 'colors' && typeof val === 'string') {
      cleaned[key] = val.split(',').map(Number).filter(Boolean)
      return
    }
    // 难度：1易/2中/3难，字符串转数字
    if (key === 'difficulty' && typeof val !== 'object') {
      const n = Number(val)
      if (!Number.isNaN(n)) cleaned[key] = n
      return
    }
    cleaned[key] = val
  })
  return request.post<SprintForecastData>('/api/postsV2/sprintForecastList', cleaned)
}

/**
 * 速记分页
 * POST /api/postsV2/highFrequency/speedMemo
 */
export const getSpeedMemoApi = (payload: Record<string, unknown>) => {
  const cleaned: Record<string, unknown> = {}
  Object.keys(payload).forEach((key) => {
    const val = payload[key]
    if (val === undefined || val === null || val === '') return
    if (key === 'hots' && typeof val === 'string') {
      cleaned[key] = val.split(',').map(Number).filter(Boolean)
      return
    }
    if (key === 'colors' && typeof val === 'string') {
      cleaned[key] = val.split(',').map(Number).filter(Boolean)
      return
    }
    // 难度：1易/2中/3难，字符串转数字
    if (key === 'difficulty' && typeof val !== 'object') {
      const n = Number(val)
      if (!Number.isNaN(n)) cleaned[key] = n
      return
    }
    cleaned[key] = val
  })
  return request.post<SpeedMemoData>('/api/postsV2/highFrequency/speedMemo', cleaned)
}

/**
 * 指南左表配置
 * GET /api/postsV2/questionTypeGuideConfig
 */
export const getGuideConfigApi = () => {
  return request.get<GuideConfigData>('/api/postsV2/questionTypeGuideConfig')
}

// ==================== 公共类型 ====================

/** 近期命中详情 */
export interface RecentHitDetail {
  /** 命中日期 yyyy-MM-dd */
  date: string
  /** 考点（可能缺省） */
  examPoint?: string
  /** 该日 forecast_count */
  count: number
}

/** 列表行公共字段（Tab1/Tab2/Tab3 共用） */
export interface QList {
  id: number
  wpId: number
  title: string
  postNumber: number
  /** 考频 3/2/1 */
  hot: number
  /** 难度 1/2/3，未标注时字段缺省 */
  difficulty?: 1 | 2 | 3
  /** 近 15 天命中次数合计 */
  recentHitCount?: number
  /** 最近最多 5 条命中详情 */
  recentHitDetails?: RecentHitDetail[]
  /** 本自然月命中次数合计 */
  monthHitCount?: number
  /** 用户收藏色（字符串） */
  color?: string
  /** 用户收藏色（数字） */
  collectColor?: number
  /** 当前用户练习次数 */
  practiceCount: number
  /** 是否已练习 0否 1是 */
  status?: number
  /** 用户是否考过 */
  exam?: number
  /** 用户是否标记 */
  read?: number
  /** 是否新题 0/1 */
  newTopic: number
  /** 是否内容更新 0/1 */
  updateTopic: number
  /** 是否旧题重考 0/1 */
  repeatedlyPass: number
  /** 高频带练微课标志 */
  highfrequencyVideo?: boolean
  /** 是否完整可练 */
  complete: 1 | 0
  /** 是否变形 0否 1是 */
  isVariant?: number
  /** DI 图表类型 */
  imageType?: string
  /** RS/WFD 场景名（话题） */
  classificationName?: string
  /** RS 句型 */
  postsSentence?: number
  /** 题型全称编码 */
  questionsTypeCode: string
  /** 中文名 */
  questionsTypeCodeDesc?: string
  /** slug */
  slugDesc?: string
  /** 短码展示 */
  name?: string
  /** 科目 */
  parentCode: string
  /** 预测时间（毫秒时间戳） */
  predictedTime?: number
  /** 创建时间（毫秒时间戳） */
  date?: number
  /** 更新时间（毫秒时间戳） */
  postModified?: number
  /** 题型数字 ID */
  categories?: number
  code?: string
  /** 场景分类（前端筛选用） */
  sceneClassification?: string
  /** 机经 id（题目详情路由参数，接口实际返回） */
  questionsId?: number
}

export interface LastPracticed {
  wpId: number
  questionId: number
  postTitle: string
  postNumber?: number
  category: number
  parentCode: string
  questionsTypeCode: string
  questionsTypeCodeDesc?: string
  name?: string
  slugDesc?: string
  slugs?: string
  practiceCount?: number
  complete?: number
  isVariant?: number
  repeatedlyPass?: number
  status?: number
}

/** Tab1 列表响应 */
export interface QListData {
  total: number
  list: QList[]
  lastPracticed?: LastPracticed
  learnCount: number
  frequencyTotal: number
}

/** 周胶囊 */
export interface WeekTab {
  /** 该周周一 yyyy-MM-dd，切周时原样回传 */
  weekMonday: string
  /** 展示 MM.dd-MM.dd */
  weekLabel: string
  /** 是否本自然周 */
  currentWeek: boolean
  /** 是否本周（历史字段名，模板中兼容使用） */
  isCurrentWeek?: boolean
  /** 该周冲刺或变动题量 */
  count: number
}

/** 题型胶囊（Tab2/3） */
export interface TypeCount {
  termId: number
  /** 短码，点胶囊作为 code */
  shortType: string
  /** 科目 */
  parentCode: string
  /** 该周该题型题量，0 也展示 */
  count: number
}

/** Tab2/3 冲刺/变动列表响应 */
export interface SprintForecastData {
  total: number
  list: QList[]
  /** 4 周，从本周往前 */
  weekTabs: WeekTab[]
  /** 15 题型胶囊 */
  typeCounts: TypeCount[]
}

/** 速记材料段 */
export interface MemoMaterial {
  text?: string
  audioUrl?: string
  imageUrl?: string
  videoUrl?: string
}

/** 速记列表项（在 QList 基础上增加 material/translation/answer/postContent/postContentFiltered） */
export interface SpeedMemoItem extends QList {
  /** 行内题型展示短码（如 RA/FIBD/FIBDD/WFD）；全部题型速记（code=ALL）逐题返回，单题型速记沿用请求短码 */
  shortType?: string
  /** 原文/题干 */
  material?: MemoMaterial
  /** 翻译 */
  translation?: MemoMaterial
  /** 答案/解析 */
  answer?: MemoMaterial
  /** 原文原始内容（富文本字符串，RTS 等题型原文取此字段） */
  postContent?: string
  /** 解析整包（富文本字符串，仅个别题型单独适配时使用） */
  postContentFiltered?: string
}

/** 答案/解析改用 postContentFiltered 整包的题型（slugDesc 短码） */
const POST_CONTENT_ANSWER_TYPES = ['RS', 'DI', 'WE', 'FIBDD', 'WFD']

/**
 * 速记列表答案字段映射：指定题型取 postContentFiltered 整包包装为 { text }
 * （下游题型组件以 v-html 整体渲染富文本），其余题型沿用接口 answer 字段。
 * typeCode 为当前列表/页签的题型码，优先于行内 shortType/slugDesc：部分「同FIBD」等变体题
 * 接口返回的 slugDesc 会标错（如标成 SWT），按题目级题型码判断会漏掉导致答案不展示；
 * 全部题型速记（code=ALL）时 typeCode 为空，按文档 4.8 用行内 shortType 逐题判断
 */
export const mapMemoAnswer = (item: SpeedMemoItem, typeCode?: string): MemoMaterial | undefined => {
  const code = (typeCode || item.shortType || item.slugDesc || '').toUpperCase()
  if (POST_CONTENT_ANSWER_TYPES.includes(code) && item.postContentFiltered) {
    return { text: item.postContentFiltered }
  }
  return item.answer
}

/** 原文/题干改用 postContent 的题型（slugDesc 短码） */
const POST_CONTENT_MATERIAL_TYPES = ['RTS']

/**
 * 速记列表原文字段映射：指定题型文本取 postContent 包装为 { text }
 * （接口 material 字段为空或不规范），音频/视频仍沿用接口 material 字段；
 * 其余题型沿用接口 material 字段。
 * typeCode 为当前列表/页签的题型码，优先于行内 shortType/slugDesc（原因同 mapMemoAnswer）
 */
export const mapMemoMaterial = (
  item: SpeedMemoItem,
  typeCode?: string
): MemoMaterial | undefined => {
  const code = (typeCode || item.shortType || item.slugDesc || '').toUpperCase()
  if (POST_CONTENT_MATERIAL_TYPES.includes(code) && item.postContent) {
    return {
      text: item.postContent,
      audioUrl: item.material?.audioUrl,
      videoUrl: item.material?.videoUrl
    }
  }
  return item.material
}

/** 速记响应 */
export interface SpeedMemoData {
  total: number
  page: number
  perPage: number
  list: SpeedMemoItem[]
  /** 仅 SPRINT 场景返回 */
  weekTabs?: WeekTab[] | null
  /** 仅 SPRINT 场景返回 */
  typeCounts?: TypeCount[] | null
  /** 该题型已练数（与列表接口 highFrequencyList 的 learnCount 同层级、同口径，用于 mini 看板 practiceProgress） */
  learnCount?: number
  /** 该题型高频总量（与列表接口 highFrequencyList 的 frequencyTotal 同层级、同口径，用于 mini 看板 totalProgress） */
  frequencyTotal?: number
}

/** 指南左表类型项 */
export interface GuideTypeItem {
  termId: number
  shortType: string
  parentCode?: string
  sort: number
  questionCountText?: string
  hitRateText?: string
  /** MUST_PRACTICE 必刷 / MUST_SEE 必看 */
  priority?: string
  /** 1=可点灯泡进右侧 */
  hasDetailCard: number
  /** 额外入口展示名 */
  title?: string
}

/** 指南左表配置响应 */
export interface GuideConfigData {
  types: GuideTypeItem[]
  extraEntries: GuideTypeItem[]
}
